import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import * as React from 'react';

import { Button, ButtonVariant } from '@ui/components/base/button';
import { XIcon } from '@ui/components/icons';
import { cn } from '@ui/lib/utils';
import { ZIndex } from '@ui/zIndexUtils';

type DialogProps = DialogPrimitive.Root.Props;

const Dialog = ({ ...props }: DialogProps) => <DialogPrimitive.Root data-slot="dialog" {...props} />;

type DialogTriggerProps = DialogPrimitive.Trigger.Props;

const DialogTrigger = ({ ...props }: DialogTriggerProps) => (
  <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
);

type DialogPortalProps = DialogPrimitive.Portal.Props;

const DialogPortal = ({ ...props }: DialogPortalProps) => (
  <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
);

type DialogCloseProps = DialogPrimitive.Close.Props;

const DialogClose = ({ ...props }: DialogCloseProps) => <DialogPrimitive.Close data-slot="dialog-close" {...props} />;

type DialogOverlayProps = DialogPrimitive.Backdrop.Props;

const DialogOverlay = ({ className, ...props }: DialogOverlayProps) => (
  <DialogPrimitive.Backdrop
    data-slot="dialog-overlay"
    className={cn(
      'fixed inset-0 isolate z-50 bg-black/10 duration-100',
      'supports-backdrop-filter:backdrop-blur-xs',
      'data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
      className
    )}
    {...props}
  />
);

interface DialogContentProps extends DialogPrimitive.Popup.Props {
  showCloseButton?: boolean;
  closeButtonClassName?: string;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  zIndex?: number;
}

const DialogContent = ({
  className,
  children,
  showCloseButton = true,
  closeButtonClassName,
  style,
  zIndex = ZIndex.DIALOG,
  ...props
}: DialogContentProps) => (
  <DialogPortal>
    <DialogOverlay style={{ zIndex }} />
    <DialogPrimitive.Popup
      data-slot="dialog-content"
      style={{ borderRadius: 'var(--r-xl)', zIndex, ...style }}
      className={cn(
        'fixed top-1/2 left-1/2 z-50 grid w-full -translate-x-1/2 -translate-y-1/2 gap-4',
        'max-w-[calc(100%-2rem)] bg-popover p-4 text-sm text-popover-foreground',
        'ring-1 ring-foreground/10 duration-100 outline-none sm:max-w-sm',
        'data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95',
        'data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close
          data-slot="dialog-close"
          render={
            <Button
              variant={ButtonVariant.GHOST}
              className={cn('absolute top-2 right-2', closeButtonClassName)}
              size="icon-sm"
            />
          }
        >
          <XIcon />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Popup>
  </DialogPortal>
);

type DialogHeaderProps = React.ComponentProps<'div'>;

const DialogHeader = ({ className, ...props }: DialogHeaderProps) => (
  <div data-slot="dialog-header" className={cn('flex flex-col gap-2', className)} {...props} />
);

interface DialogFooterProps extends React.ComponentProps<'div'> {
  showCloseButton?: boolean;
}

const DialogFooter = ({ className, showCloseButton = false, children, ...props }: DialogFooterProps) => (
  <div
    data-slot="dialog-footer"
    className={cn(
      '-mx-4 -mb-4 flex flex-col-reverse gap-2 rounded-b-[20px] border-t border-border p-4 sm:flex-row sm:justify-end',
      className
    )}
    {...props}
  >
    {children}
    {showCloseButton && (
      <DialogPrimitive.Close render={<Button variant={ButtonVariant.OUTLINE} />}>Close</DialogPrimitive.Close>
    )}
  </div>
);

type DialogTitleProps = DialogPrimitive.Title.Props;

const DialogTitle = ({ className, ...props }: DialogTitleProps) => (
  <DialogPrimitive.Title
    data-slot="dialog-title"
    className={cn('text-base leading-none font-medium', className)}
    {...props}
  />
);

type DialogDescriptionProps = DialogPrimitive.Description.Props;

const DialogDescription = ({ className, ...props }: DialogDescriptionProps) => (
  <DialogPrimitive.Description
    data-slot="dialog-description"
    className={cn(
      'text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground',
      className
    )}
    {...props}
  />
);

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
